/**
 * scanWedge — tell a USB / Bluetooth scanner's typing from a person's (2026-10-21).
 *
 * A "keyboard wedge" scanner types the code's text and presses Enter. The shop's scanner
 * (Alacrity 2D desktop = Newtologic NT4010E, fixtures/scannerNT4010E.js) sends its
 * characters 0-27 ms apart, then one Enter ~15 ms after the last, with NumLock taps
 * (macOS: "Clear") before and after. A person types 100+ ms apart. So a scan is: a run of
 * characters each <= SCAN_MAX_GAP_MS after the one before, at least SCAN_MIN_CHARS long,
 * ended by an Enter <= SCAN_MAX_GAP_MS later, whose text is a JobQ code (parseScanCode).
 * A bare code ("job 9403", no /s/ link) is short enough for a fast typist to hit, so it must
 * come at scanner speed throughout (<= SCAN_BARE_MAX_GAP_MS); every printed QR is a link.
 * Times are KeyboardEvent.timeStamp (when the key arrived, not when the page got to it).
 * The scanner types digits on the main row (code Digit1, not Numpad1), so NumLock's state
 * doesn't change what arrives.
 *
 * The pure half; components/ScanWedge.jsx listens to the whole app with it.
 */
import { parseScanCode } from "./scanCodes";

export const SCAN_MAX_GAP_MS = 60;        // twice the scanner's slowest gap (27 ms)
export const SCAN_BARE_MAX_GAP_MS = 35;   // a bare code: scanner speed only
export const SCAN_MIN_CHARS = 6;          // a printed code is a whole link; "task-1" is the shortest bare code
export const SCAN_REPEAT_MS = 3000;       // a hands-free scanner keeps reading paper left in front of it

// Keys a scanner sends around its text that neither add a character nor end the run.
const PASS_THROUGH_KEYS = new Set(["Shift", "Clear", "NumLock", "CapsLock"]);

/**
 * Feed it every keydown, in order. key(ev) answers:
 *   {type: "start", prevRun, sincePrev}
 *                                 this character began a new fast run (the field it lands in
 *                                 still holds what it had before the run: snapshot it now).
 *                                 prevRun = length of the run before it (0 after a reset),
 *                                 sincePrev = ms since that run's last character.
 *   {type: "char"}                another character of the current run
 *   {type: "scan", parsed, text}  an Enter that ended a scan of a JobQ code
 *   {type: "other"}               anything else (an ordinary Enter, Tab, a shortcut, a pass-through key)
 * ev needs key, timeStamp, ctrlKey, metaKey, altKey (and isComposing). `parse` decides what counts as a code
 * (default parseScanCode; the laser kiosk takes any text and lets the API say what it is). `joinHiccupMs` keeps a
 * run of 3+ characters going across a pause up to that long, so a hiccup mid-scan is still one scan.
 */
export function createScanDetector({
  maxGapMs = SCAN_MAX_GAP_MS, bareMaxGapMs = SCAN_BARE_MAX_GAP_MS, minChars = SCAN_MIN_CHARS, parse = parseScanCode,
  joinHiccupMs = 0,
} = {}) {
  let text = "";
  let lastAt = null;
  let maxGap = 0;

  function reset() {
    text = "";
    lastAt = null;
    maxGap = 0;
  }

  function key(ev) {
    if (!ev || ev.isComposing) {
      reset();
      return { type: "other" };
    }
    if (PASS_THROUGH_KEYS.has(ev.key)) return { type: "other" };
    const t = ev.timeStamp;

    if (ev.key === "Enter") {
      const gap = lastAt === null ? Infinity : t - lastAt;
      const run = text;
      const runMaxGap = Math.max(maxGap, gap);
      reset();
      if (gap > maxGapMs || run.length < minChars || ev.ctrlKey || ev.metaKey || ev.altKey) return { type: "other" };
      const parsed = parse(run);
      if (!parsed) return { type: "other" };
      if (!/\/s\//i.test(run) && runMaxGap > bareMaxGapMs) return { type: "other" };
      return { type: "scan", parsed, text: run };
    }

    const isChar = typeof ev.key === "string" && ev.key.length === 1 && !ev.ctrlKey && !ev.metaKey && !ev.altKey;
    if (!isChar) {
      reset();
      return { type: "other" };
    }
    if (joinHiccupMs && lastAt !== null && t - lastAt > maxGapMs && t - lastAt <= joinHiccupMs && text.length >= 3) {
      text += ev.key;
      lastAt = t;
      return { type: "char" };
    }
    if (lastAt === null || t - lastAt > maxGapMs) {
      const answer = { type: "start", prevRun: text.length, sincePrev: lastAt === null ? Infinity : t - lastAt };
      text = ev.key;
      lastAt = t;
      maxGap = 0;
      return answer;
    }
    maxGap = Math.max(maxGap, t - lastAt);
    text += ev.key;
    lastAt = t;
    return { type: "char" };
  }

  return { key, reset };
}

/** True when this is the code scanned last, again within SCAN_REPEAT_MS. */
export function isRepeatScan(last, parsed, at, windowMs = SCAN_REPEAT_MS) {
  return !!(last && parsed && last.code === parsed.code && at - last.at >= 0 && at - last.at < windowMs);
}

/**
 * True while a dialog is open: a Bootstrap modal (the PIN prompt, cause / stick-lot pickers,
 * page modals) or a phone panel. A scan must not change the page underneath one.
 */
export function dialogOpen(doc = document) {
  return !!doc.querySelector('.modal.show, [aria-modal="true"]');
}

// Inputs a scanner can type into (a checkbox or a button can't take text).
const TEXT_INPUT_TYPES = new Set(["", "text", "search", "url", "tel", "email", "password", "number"]);

/** What a text field held before a scan typed into it, or null when focus isn't in one. */
export function snapshotField(el) {
  if (!el || !el.tagName) return null;
  const tag = el.tagName.toUpperCase();
  const isText = tag === "TEXTAREA" || (tag === "INPUT" && TEXT_INPUT_TYPES.has((el.getAttribute("type") || "").toLowerCase()));
  if (!isText || el.readOnly || el.disabled) return null;
  let selection = null;
  try {
    selection = [el.selectionStart, el.selectionEnd];
  } catch (_) {
    // number / email inputs have no selection API
  }
  return { el, value: el.value, selection };
}

/**
 * Put a field back the way snapshotField saw it, through the native value setter plus an
 * input event, so a React-controlled field's state follows (setting .value alone would be
 * undone by React on its next render).
 */
export function restoreField(snap) {
  if (!snap || !snap.el || !snap.el.isConnected) return;
  const { el } = snap;
  // A number input that was typed letters reads "" while it still shows them: reset it anyway.
  if (el.value === snap.value && (el.getAttribute("type") || "").toLowerCase() !== "number") return;
  const proto = el.tagName.toUpperCase() === "TEXTAREA" ? window.HTMLTextAreaElement.prototype : window.HTMLInputElement.prototype;
  const setter = Object.getOwnPropertyDescriptor(proto, "value").set;
  setter.call(el, snap.value);
  el.dispatchEvent(new Event("input", { bubbles: true }));
  if (snap.selection && snap.selection[0] !== null) {
    try {
      el.setSelectionRange(snap.selection[0], snap.selection[1]);
    } catch (_) {
      // not every input type allows it
    }
  }
}
